import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import api from '../../utils/api';
import { toast } from 'sonner';
import { 
  Search, Package, RefreshCw, Loader2, Trash2, Download, AlertCircle, 
  Plus, X, Upload, Terminal, Box, FileBox, ExternalLink, Check
} from 'lucide-react';
import { formatDate, parseDateFromYYYYMMDD } from '../../utils/dateFormatter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const AppsTab = ({ agentId }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [updates, setUpdates] = useState({});
  const [search, setSearch] = useState('');
  const [uninstalling, setUninstalling] = useState({});
  const [updating, setUpdating] = useState({});
  const [regionalSettings, setRegionalSettings] = useState({});
  const [showOnlyUpdates, setShowOnlyUpdates] = useState(false);
  
  // Install modal state
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [installMethod, setInstallMethod] = useState('winget');
  const [packageSearch, setPackageSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [installing, setInstalling] = useState(false);
  
  // Manual install state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [commandLineArgs, setCommandLineArgs] = useState('');
  const [silentInstall, setSilentInstall] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchApps(false);
    loadCachedUpdates();
    fetchRegionalSettings();
  }, [agentId]);

  const fetchRegionalSettings = async () => {
    try {
      const res = await api.get('/settings');
      setRegionalSettings({
        date_format: res.data.date_format || 'DD/MM/YYYY',
        timezone: res.data.timezone || 'UTC'
      });
    } catch (error) {
      // Fail silently, use defaults
    }
  };

  const fetchApps = async (refresh = false) => {
    if (refresh) {
      setLoading(true);
    }
    try {
      const res = await api.get(`/agents/${agentId}/apps${refresh ? '?refresh=true' : ''}`);
      setApps(res.data.apps || []);
      if (refresh && res.data.refreshed) {
        toast.success('Applications list refreshed!');
      }
    } catch (error) {
      toast.error('Failed to load installed applications');
    } finally {
      setLoading(false);
    }
  };

  const loadCachedUpdates = async () => {
    try {
      const res = await api.get(`/agents/${agentId}/apps/updates`);
      const updatesData = res.data.updates || [];
      
      const updatesMap = {};
      updatesData.forEach(update => {
        updatesMap[update.app_name] = {
          current_version: update.current_version,
          available_version: update.available_version,
          id: update.app_id,
          checked_at: update.checked_at
        };
        
        updatesMap[`id:${update.app_id}`] = {
          current_version: update.current_version,
          available_version: update.available_version,
          id: update.app_id,
          checked_at: update.checked_at,
          winget_name: update.app_name
        };
      });
      
      setUpdates(updatesMap);
    } catch (error) {
      console.error('Failed to load cached updates:', error);
    }
  };

  const checkForUpdates = async () => {
    setCheckingUpdates(true);
    try {
      const res = await api.post(`/agents/${agentId}/apps/check-updates`);
      const updatesData = res.data.updates || [];
      
      const updatesMap = {};
      updatesData.forEach(update => {
        updatesMap[update.name] = {
          current_version: update.current_version,
          available_version: update.available_version,
          id: update.id
        };
      });
      
      setUpdates(updatesMap);
      if (updatesData.length > 0) {
        toast.success(`Found ${updatesData.length} available updates`);
      } else {
        toast.success('All applications are up to date');
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
      toast.error('Failed to check for updates');
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleUninstall = async (appName) => {
    if (!window.confirm(`Are you sure you want to uninstall ${appName}?`)) {
      return;
    }

    setUninstalling(prev => ({ ...prev, [appName]: true }));
    try {
      await api.post(`/agents/${agentId}/apps/uninstall?app_name=${encodeURIComponent(appName)}`);
      toast.success(`Uninstall command sent for ${appName}`);
      setTimeout(() => fetchApps(), 3000);
    } catch (error) {
      toast.error('Failed to send uninstall command');
    } finally {
      setUninstalling(prev => ({ ...prev, [appName]: false }));
    }
  };

  const handleUpdate = async (appName, appId) => {
    if (!appId) {
      toast.error('Missing app ID - cannot update');
      return;
    }
    
    setUpdating(prev => ({ ...prev, [appName]: true }));
    
    try {
      await api.post(`/agents/${agentId}/apps/update?app_id=${encodeURIComponent(appId)}`);
      toast.success(`Update command sent for ${appName}`);
      setTimeout(() => {
        fetchApps();
        checkForUpdates();
      }, 5000);
    } catch (error) {
      toast.error(`Failed to send update command: ${error.message || 'Unknown error'}`);
    } finally {
      setUpdating(prev => ({ ...prev, [appName]: false }));
    }
  };

  // Search packages (Winget or Chocolatey)
  const searchPackages = async () => {
    if (!packageSearch.trim()) return;
    
    setSearching(true);
    setSearchResults([]);
    setSelectedPackage(null);
    
    try {
      const res = await api.post(`/agents/${agentId}/apps/search`, {
        query: packageSearch,
        source: installMethod
      });
      
      const data = res.data;
      
      if (data.immediate && data.status === 'completed') {
        setSearchResults(data.packages || []);
        if (data.packages?.length === 0) {
          toast.info('No packages found matching your search');
        }
      } else if (data.status === 'queued') {
        toast.warning('Search queued - agent will process on next check-in');
      } else {
        toast.error(data.message || 'Search failed');
      }
    } catch (error) {
      console.error('Package search error:', error);
      toast.error('Failed to search packages');
    } finally {
      setSearching(false);
    }
  };

  // Install package from Winget or Chocolatey
  const installPackage = async () => {
    if (!selectedPackage) return;
    
    setInstalling(true);
    
    try {
      const res = await api.post(`/agents/${agentId}/apps/install`, {
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        source: installMethod,
        silent: silentInstall
      });
      
      const data = res.data;
      
      if (data.immediate && data.status === 'completed') {
        toast.success(`Installation started for ${selectedPackage.name}`);
        setInstallModalOpen(false);
        resetInstallModal();
        setTimeout(() => fetchApps(true), 5000);
      } else if (data.status === 'queued') {
        toast.warning(`Install queued for ${selectedPackage.name} - will execute on next agent check-in`);
        setInstallModalOpen(false);
        resetInstallModal();
      } else {
        toast.error(data.message || 'Installation failed');
      }
    } catch (error) {
      console.error('Install error:', error);
      toast.error('Failed to install package');
    } finally {
      setInstalling(false);
    }
  };

  // Handle file upload for manual install
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const validExtensions = ['.msi', '.exe'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExt)) {
      toast.error('Only .msi and .exe files are supported');
      return;
    }
    
    if (file.size > 500 * 1024 * 1024) { // 500MB limit
      toast.error('File size must be less than 500MB');
      return;
    }
    
    setUploadedFile(file);
    
    // Set default silent install args based on file type
    if (fileExt === '.msi') {
      setCommandLineArgs('/qn /norestart');
    } else {
      setCommandLineArgs('/S');
    }
  };

  // Install uploaded file
  const installUploadedFile = async () => {
    if (!uploadedFile) return;
    
    setInstalling(true);
    setUploadProgress(0);
    
    try {
      // First, upload the file
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('command_args', commandLineArgs);
      formData.append('silent', silentInstall.toString());
      
      const res = await api.post(`/agents/${agentId}/apps/install-manual`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const data = res.data;
      
      if (data.immediate && data.status === 'completed') {
        toast.success(`Installation started for ${uploadedFile.name}`);
        setInstallModalOpen(false);
        resetInstallModal();
        setTimeout(() => fetchApps(true), 10000);
      } else if (data.status === 'queued') {
        toast.warning(`Install queued for ${uploadedFile.name} - will execute on next agent check-in`);
        setInstallModalOpen(false);
        resetInstallModal();
      } else {
        toast.error(data.message || 'Installation failed');
      }
    } catch (error) {
      console.error('Manual install error:', error);
      toast.error('Failed to install file');
    } finally {
      setInstalling(false);
      setUploadProgress(0);
    }
  };

  // Reset install modal state
  const resetInstallModal = () => {
    setInstallMethod('winget');
    setPackageSearch('');
    setSearchResults([]);
    setSelectedPackage(null);
    setUploadedFile(null);
    setCommandLineArgs('');
    setSilentInstall(true);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to check if app has update
  const getAppUpdate = (app) => {
    if (!app.name) return null;
    if (updates[app.name]) return updates[app.name];
    
    if (app.version) {
      const appVersion = app.version.trim();
      const updateKeys = Object.keys(updates).filter(k => !k.startsWith('id:'));
      
      for (const key of updateKeys) {
        const update = updates[key];
        if (update.current_version && update.current_version.trim() === appVersion) {
          return update;
        }
      }
    }
    
    return null;
  };

  const filteredApps = apps
    .filter(app => {
      const matchesSearch = app.name?.toLowerCase().includes(search.toLowerCase()) ||
        app.publisher?.toLowerCase().includes(search.toLowerCase());
      
      const hasUpdate = getAppUpdate(app);
      const matchesUpdateFilter = !showOnlyUpdates || hasUpdate;
      
      return matchesSearch && matchesUpdateFilter;
    })
    .sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={showOnlyUpdates ? "Search apps with updates..." : "Search applications..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-10 ${showOnlyUpdates ? 'border-orange-300 focus:border-orange-400' : ''}`}
          />
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setInstallModalOpen(true)} 
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4" />
            Install App
          </Button>
          <Button 
            onClick={() => fetchApps(true)} 
            variant="outline" 
            className="gap-2"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
          <Button 
            onClick={checkForUpdates} 
            variant="outline" 
            className="gap-2"
            disabled={checkingUpdates}
          >
            {checkingUpdates ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Check Updates
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Installed Applications ({filteredApps.length})
            {Object.keys(updates).filter(k => !k.startsWith('id:')).length > 0 && (
              <Badge 
                onClick={() => setShowOnlyUpdates(!showOnlyUpdates)}
                className={`${
                  showOnlyUpdates 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-orange-100 text-orange-700'
                } cursor-pointer hover:scale-105 transition-all`}
              >
                <Download className="w-3 h-3 mr-1" />
                {Object.keys(updates).filter(k => !k.startsWith('id:')).length} updates
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Application Name</th>
                  <th className="text-left py-3 px-4 font-medium">Version</th>
                  <th className="text-left py-3 px-4 font-medium">Publisher</th>
                  <th className="text-left py-3 px-4 font-medium">Install Date</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      {search ? 'No applications match your search' : 'No applications found'}
                    </td>
                  </tr>
                ) : (
                  filteredApps.map((app, index) => {
                    const hasUpdate = getAppUpdate(app);
                    const isUninstalling = uninstalling[app.name];
                    const isUpdating = updating[app.name];

                    return (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{app.name}</td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex flex-col gap-1">
                            <span className={hasUpdate ? "text-gray-600" : ""}>
                              {app.version || 'N/A'}
                            </span>
                            {hasUpdate && (
                              <span className="text-xs text-green-600 font-medium">
                                → {hasUpdate.available_version}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{app.publisher || 'Unknown'}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {app.install_date ? 
                            formatDate(parseDateFromYYYYMMDD(app.install_date) || app.install_date, regionalSettings) 
                            : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            {hasUpdate && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdate(app.name, hasUpdate.id)}
                                disabled={isUpdating}
                                className="gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                              >
                                {isUpdating ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Download className="w-3 h-3" />
                                )}
                                Update
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUninstall(app.name)}
                              disabled={isUninstalling}
                              className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {isUninstalling ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                              Uninstall
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Install App Modal */}
      <Dialog open={installModalOpen} onOpenChange={(open) => {
        setInstallModalOpen(open);
        if (!open) resetInstallModal();
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Install Application
            </DialogTitle>
            <DialogDescription>
              Choose an installation method to remotely install software on this device.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={installMethod} onValueChange={(v) => {
            setInstallMethod(v);
            setSearchResults([]);
            setSelectedPackage(null);
            setPackageSearch('');
          }} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="winget" className="gap-2">
                <Terminal className="w-4 h-4" />
                Winget
              </TabsTrigger>
              <TabsTrigger value="chocolatey" className="gap-2">
                <Box className="w-4 h-4" />
                Chocolatey
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2">
                <Upload className="w-4 h-4" />
                Manual
              </TabsTrigger>
            </TabsList>

            {/* Winget Tab */}
            <TabsContent value="winget" className="flex-1 overflow-hidden flex flex-col mt-4">
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search Winget packages (e.g., 'chrome', 'vscode')"
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchPackages()}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={searchPackages} disabled={searching || !packageSearch.trim()}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>

                <div className="flex-1 overflow-auto border rounded-lg">
                  {searchResults.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Terminal className="w-12 h-12 mb-3 text-gray-300" />
                      <p>Search for packages to install via Winget</p>
                      <p className="text-sm mt-1">Windows Package Manager</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {searchResults.map((pkg, idx) => (
                        <div
                          key={idx}
                          onClick={() => setSelectedPackage(pkg)}
                          className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedPackage?.id === pkg.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium flex items-center gap-2">
                                {pkg.name}
                                {selectedPackage?.id === pkg.id && (
                                  <Check className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div className="text-sm text-gray-500 font-mono">{pkg.id}</div>
                              {pkg.version && (
                                <div className="text-xs text-gray-400 mt-1">Version: {pkg.version}</div>
                              )}
                            </div>
                            {pkg.source && (
                              <Badge variant="outline" className="text-xs">{pkg.source}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedPackage && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-900">{selectedPackage.name}</div>
                        <div className="text-sm text-blue-700 font-mono">{selectedPackage.id}</div>
                      </div>
                      <Badge className="bg-blue-600">Selected</Badge>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Chocolatey Tab */}
            <TabsContent value="chocolatey" className="flex-1 overflow-hidden flex flex-col mt-4">
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search Chocolatey packages (e.g., 'git', 'nodejs')"
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchPackages()}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={searchPackages} disabled={searching || !packageSearch.trim()}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>

                <div className="flex-1 overflow-auto border rounded-lg">
                  {searchResults.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Box className="w-12 h-12 mb-3 text-gray-300" />
                      <p>Search for packages to install via Chocolatey</p>
                      <p className="text-sm mt-1">The package manager for Windows</p>
                      <a 
                        href="https://community.chocolatey.org/packages" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-2 flex items-center gap-1"
                      >
                        Browse packages <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {searchResults.map((pkg, idx) => (
                        <div
                          key={idx}
                          onClick={() => setSelectedPackage(pkg)}
                          className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedPackage?.id === pkg.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium flex items-center gap-2">
                                {pkg.name}
                                {selectedPackage?.id === pkg.id && (
                                  <Check className="w-4 h-4 text-purple-600" />
                                )}
                              </div>
                              <div className="text-sm text-gray-500 font-mono">{pkg.id}</div>
                              {pkg.version && (
                                <div className="text-xs text-gray-400 mt-1">Version: {pkg.version}</div>
                              )}
                              {pkg.description && (
                                <div className="text-xs text-gray-500 mt-1 line-clamp-2">{pkg.description}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedPackage && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-purple-900">{selectedPackage.name}</div>
                        <div className="text-sm text-purple-700 font-mono">{selectedPackage.id}</div>
                      </div>
                      <Badge className="bg-purple-600">Selected</Badge>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Manual Tab */}
            <TabsContent value="manual" className="flex-1 overflow-auto mt-4">
              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".msi,.exe"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {uploadedFile ? (
                    <div className="space-y-3">
                      <FileBox className="w-12 h-12 mx-auto text-green-600" />
                      <div>
                        <div className="font-medium text-lg">{uploadedFile.name}</div>
                        <div className="text-sm text-gray-500">
                          {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadedFile(null);
                          setCommandLineArgs('');
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="gap-1"
                      >
                        <X className="w-4 h-4" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 mx-auto text-gray-400" />
                      <div>
                        <p className="font-medium">Upload Installer</p>
                        <p className="text-sm text-gray-500">MSI or EXE files up to 500MB</p>
                      </div>
                      <Button onClick={() => fileInputRef.current?.click()}>
                        Select File
                      </Button>
                    </div>
                  )}
                  
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-4">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <div className="text-sm text-gray-500 mt-1">Uploading... {uploadProgress}%</div>
                    </div>
                  )}
                </div>

                {uploadedFile && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Command Line Arguments (optional)
                      </label>
                      <Textarea
                        value={commandLineArgs}
                        onChange={(e) => setCommandLineArgs(e.target.value)}
                        placeholder={uploadedFile.name.endsWith('.msi') 
                          ? '/qn /norestart' 
                          : '/S /silent /verysilent'
                        }
                        rows={2}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Common silent install args: MSI: <code>/qn /norestart</code> | EXE: <code>/S</code> or <code>/silent</code>
                      </p>
                    </div>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={silentInstall}
                        onChange={(e) => setSilentInstall(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">Silent install (recommended)</span>
                    </label>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setInstallModalOpen(false);
              resetInstallModal();
            }}>
              Cancel
            </Button>
            {installMethod === 'manual' ? (
              <Button 
                onClick={installUploadedFile} 
                disabled={!uploadedFile || installing}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {installing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Installing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload & Install
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={installPackage} 
                disabled={!selectedPackage || installing}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {installing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Installing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Install {selectedPackage?.name || 'Package'}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppsTab;
